import React, { useEffect, useRef } from 'react';
import twemoji from 'twemoji';

export const IOS_ICONS_MAP = {
};

/**
 * Creates a Twemoji-based icon component for consistent cross-platform rendering
 * @param {string} emoji - The emoji character
 * @param {string} ariaLabel - Accessibility label
 * @returns {React.Component} - A React component that renders the emoji using Twemoji
 */
const createTwemojiIcon = (emoji, ariaLabel) => {
  const TwemojiIcon = ({ size = 20, className = '' }) => {
    const emojiRef = useRef(null);

    useEffect(() => {
      if (emojiRef.current) {
        twemoji.parse(emojiRef.current, {
          folder: 'svg',
          ext: '.svg',
          base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@latest/assets/',
        });
      }
    }, []);

    return (
      <span
        ref={emojiRef}
        className={className}
        style={{ 
          fontSize: size, 
          lineHeight: 1, 
          display: 'inline-block',
          width: size,
          height: size,
        }}
        role="img"
        aria-label={ariaLabel}
      >
        {emoji}
      </span>
    );
  };
  
  return TwemojiIcon;
};

const HeartEyesIcon = createTwemojiIcon('😍', 'smiling face with heart-eyes');
const SmilingFaceIcon = createTwemojiIcon('😊', 'smiling face with smiling eyes');
const ExpressionlessFaceIcon = createTwemojiIcon('😑', 'expressionless face');
const FrowningFaceIcon = createTwemojiIcon('☹️', 'frowning face');
const PensiveFaceIcon = createTwemojiIcon('😔', 'pensive face');
const GrinningFaceIcon = createTwemojiIcon('😀', 'grinning face');
const BeamingFaceIcon = createTwemojiIcon('😁', 'beaming face with smiling eyes');
const GrinningSquintingIcon = createTwemojiIcon('😆', 'grinning squinting face');
const WinkingFaceIcon = createTwemojiIcon('😉', 'winking face');
const SmilingFaceWithHeartsIcon = createTwemojiIcon('🥰', 'smiling face with hearts');
const SmilingFaceWithHaloIcon = createTwemojiIcon('😇', 'smiling face with halo');
const PartyingFaceIcon = createTwemojiIcon('🥳', 'partying face');
const ThinkingFaceIcon = createTwemojiIcon('🤔', 'thinking face');
const UnamusedFaceIcon = createTwemojiIcon('😒', 'unamused face');
const RollingEyesIcon = createTwemojiIcon('🙄', 'face with rolling eyes');
const ConfusedFaceIcon = createTwemojiIcon('😕', 'confused face');
const WorriedFaceIcon = createTwemojiIcon('😟', 'worried face');
const CryingFaceIcon = createTwemojiIcon('😢', 'crying face');
const LoudlyCryingIcon = createTwemojiIcon('😭', 'loudly crying face');
const AngryFaceIcon = createTwemojiIcon('😠', 'angry face');
const PoutingFaceIcon = createTwemojiIcon('😡', 'pouting face');
const FearfulFaceIcon = createTwemojiIcon('😨', 'fearful face');
const AstonishedFaceIcon = createTwemojiIcon('😲', 'astonished face');
const FlushedFaceIcon = createTwemojiIcon('😳', 'flushed face');
const TiredFaceIcon = createTwemojiIcon('😫', 'tired face');
const WearyFaceIcon = createTwemojiIcon('😩', 'weary face');
const SmilingFaceWithSunglassesIcon = createTwemojiIcon('😎', 'smiling face with sunglasses');
const UpsideDownFaceIcon = createTwemojiIcon('🙃', 'upside-down face');
const RelievedFaceIcon = createTwemojiIcon('😌', 'relieved face');
const DisappointedFaceIcon = createTwemojiIcon('😞', 'disappointed face');
const StarIcon = createTwemojiIcon('⭐', 'star');

IOS_ICONS_MAP.heartEyes = HeartEyesIcon;
IOS_ICONS_MAP.smilingFace = SmilingFaceIcon;
IOS_ICONS_MAP.expressionlessFace = ExpressionlessFaceIcon;
IOS_ICONS_MAP.frowningFace = FrowningFaceIcon;
IOS_ICONS_MAP.pensiveFace = PensiveFaceIcon;
IOS_ICONS_MAP.grinningFace = GrinningFaceIcon;
IOS_ICONS_MAP.beamingFace = BeamingFaceIcon;
IOS_ICONS_MAP.grinningSquinting = GrinningSquintingIcon;
IOS_ICONS_MAP.winkingFace = WinkingFaceIcon;
IOS_ICONS_MAP.smilingFaceWithHearts = SmilingFaceWithHeartsIcon;
IOS_ICONS_MAP.smilingFaceWithHalo = SmilingFaceWithHaloIcon;
IOS_ICONS_MAP.partyingFace = PartyingFaceIcon;
IOS_ICONS_MAP.thinkingFace = ThinkingFaceIcon;
IOS_ICONS_MAP.unamusedFace = UnamusedFaceIcon;
IOS_ICONS_MAP.rollingEyes = RollingEyesIcon;
IOS_ICONS_MAP.confusedFace = ConfusedFaceIcon;
IOS_ICONS_MAP.worriedFace = WorriedFaceIcon;
IOS_ICONS_MAP.cryingFace = CryingFaceIcon;
IOS_ICONS_MAP.loudlyCrying = LoudlyCryingIcon;
IOS_ICONS_MAP.angryFace = AngryFaceIcon;
IOS_ICONS_MAP.poutingFace = PoutingFaceIcon;
IOS_ICONS_MAP.fearfulFace = FearfulFaceIcon;
IOS_ICONS_MAP.astonishedFace = AstonishedFaceIcon;
IOS_ICONS_MAP.flushedFace = FlushedFaceIcon;
IOS_ICONS_MAP.tiredFace = TiredFaceIcon;
IOS_ICONS_MAP.wearyFace = WearyFaceIcon;
IOS_ICONS_MAP.smilingFaceWithSunglasses = SmilingFaceWithSunglassesIcon;
IOS_ICONS_MAP.upsideDownFace = UpsideDownFaceIcon;
IOS_ICONS_MAP.relievedFace = RelievedFaceIcon;
IOS_ICONS_MAP.disappointedFace = DisappointedFaceIcon;
IOS_ICONS_MAP.star = StarIcon;

export const IOS_ICON_OPTIONS = [
  { value: 'heartEyes', label: '😍' },
  { value: 'smilingFace', label: '😊' },
  { value: 'expressionlessFace', label: '😑' },
  { value: 'frowningFace', label: '☹️' },
  { value: 'pensiveFace', label: '😔' },
  { value: 'grinningFace', label: '😀' },
  { value: 'beamingFace', label: '😁' },
  { value: 'grinningSquinting', label: '😆' },
  { value: 'winkingFace', label: '😉' },
  { value: 'smilingFaceWithHearts', label: '🥰' },
  { value: 'smilingFaceWithHalo', label: '😇' },
  { value: 'partyingFace', label: '🥳' },
  { value: 'thinkingFace', label: '🤔' },
  { value: 'unamusedFace', label: '😒' },
  { value: 'rollingEyes', label: '🙄' },
  { value: 'confusedFace', label: '😕' },
  { value: 'worriedFace', label: '😟' },
  { value: 'cryingFace', label: '😢' },
  { value: 'loudlyCrying', label: '😭' },
  { value: 'angryFace', label: '😠' },
  { value: 'poutingFace', label: '😡' },
  { value: 'fearfulFace', label: '😨' },
  { value: 'astonishedFace', label: '😲' },
  { value: 'flushedFace', label: '😳' },
  { value: 'tiredFace', label: '😫' },
  { value: 'wearyFace', label: '😩' },
  { value: 'smilingFaceWithSunglasses', label: '😎' },
  { value: 'upsideDownFace', label: '🙃' },
  { value: 'relievedFace', label: '😌' },
  { value: 'disappointedFace', label: '😞' },
  { value: 'star', label: '⭐' },
];

